import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, CardBody, Button, FormGroup, Label, Input, ListGroup, ListGroupItem, Table, Alert, Badge } from 'reactstrap';
import Select from 'react-select';
import { useToasts } from 'react-toast-notifications';
import { withTranslation } from 'react-multi-lang';
import { DionysosService } from '../Services/DionysosService';
import SchedulesService from '../Services/SchedulesService';
import QuarriesService from '../Services/QuarriesService';
import AuthService from '../Services/AuthService';
import FaultsService from '../Services/FaultsService';

const Schedules = ({ t }) => {
    const [machines, setMachines] = useState([]);
    const [allMachinesRaw, setAllMachinesRaw] = useState([]);
    const [activeFaults, setActiveFaults] = useState([]);
    const [selectedMachine, setSelectedMachine] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [dateTo, setDateTo] = useState('');
    const [availableShelves, setAvailableShelves] = useState([]);
    const [scheduledShelves, setScheduledShelves] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingScheduleId, setEditingScheduleId] = useState(null);
    const [weekSchedules, setWeekSchedules] = useState([]);
    const [isDraggingDates, setIsDraggingDates] = useState(false);
    const [dragStartDate, setDragStartDate] = useState(null);
    const [dragEndDate, setDragEndDate] = useState(null);

    // Form Filters
    const [formFilterCategory, setFormFilterCategory] = useState(null);
    const [formFilterSubcategory, setFormFilterSubcategory] = useState(null);

    // Calendar Filters
    const [calFilterCategory, setCalFilterCategory] = useState(null);
    const [calFilterSubcategory, setCalFilterSubcategory] = useState(null);
    const [calFilterMachine, setCalFilterMachine] = useState(null);
    const [calFilterShelf, setCalFilterShelf] = useState(null);
    const [weekStartDate, setWeekStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - d.getDay() + 1); // Monday
        return d.toISOString().split('T')[0];
    });
    const formRef = useRef(null);

    const dionysosService = new DionysosService();
    const schedulesService = new SchedulesService();
    const quarriesService = new QuarriesService();
    const authService = new AuthService();
    const faultsService = new FaultsService();
    const { addToast } = useToasts();

    const currentWh = authService.getConfig()?.whCode;

    useEffect(() => {
        getMachines();
        getQuarryShelves();
        getActiveFaults();
    }, [currentWh]);

    useEffect(() => {
        if (selectedMachine && selectedDate) {
            fetchSchedule();
        }
    }, [selectedMachine, selectedDate]);

    useEffect(() => {
        fetchWeekSchedules();
    }, [weekStartDate]);

    useEffect(() => {
        const handleGlobalMouseUp = () => {
            if (isDraggingDates) {
                // If we're dragging, onDayMouseUp might have been missed if mouse was released outside a cell
                setIsDraggingDates(false);
                setDragStartDate(null);
                setDragEndDate(null);
            }
        };
        window.addEventListener('mouseup', handleGlobalMouseUp);
        return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
    }, [isDraggingDates]);

    const getMachines = () => {
        dionysosService.getMachines().then(r => {
            setAllMachinesRaw(r);
            setMachines(r.map(m => ({ value: m.Code, label: m.Description || m.Code, Code1: m.Code1, Code2: m.Code2, raw: m })));
        }).catch(e => console.error(e));
    };

    const getActiveFaults = () => {
        faultsService.getFaults({ isResolved: 'false' })
            .then(data => {
                setActiveFaults(data || []);
            })
            .catch(e => console.error('Error fetching active faults:', e));
    };

    const getQuarryShelves = () => {
        if (!currentWh) return;
        
        const quarryPromise = quarriesService.getQuarryByWhCode(currentWh);
        const configShelves = authService.availableConfigs.find(c => c.whCode === currentWh)?.shelves || [];
        const mappedConfigShelves = configShelves.map(name => ({ name }));

        quarryPromise.then(r => {
            let combinedShelves = [];
            if (r.data && r.data.shelves) {
                combinedShelves = [...r.data.shelves];
            }
            
            mappedConfigShelves.forEach(cs => {
                if (!combinedShelves.find(s => s.name === cs.name)) {
                    combinedShelves.push(cs);
                }
            });

            setAvailableShelves(combinedShelves);
        }).catch(e => {
            console.error(e);
            setAvailableShelves(mappedConfigShelves);
        });
    };

    const fetchSchedule = () => {
        setLoading(true);
        schedulesService.getSchedule(selectedMachine.value, selectedDate).then(r => {
            console.log('Fetched schedule for', selectedMachine.value, 'on', selectedDate, ':', r.data);
            if (r.data && r.data.shelfNames) {
                setScheduledShelves(r.data.shelfNames.map(name => ({ name })));
                if (r.data.dateTo) setDateTo(r.data.dateTo);
                else setDateTo('');
                setEditingScheduleId(r.data.id);
            } else {
                // If no schedule found, do not clear what the user might have already selected
                // only reset the editing ID
                setEditingScheduleId(null);
            }
            setLoading(false);
        }).catch(e => {
            console.error(e);
            setLoading(false);
        });
    };

    const fetchWeekSchedules = () => {
        const start = new Date(weekStartDate);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        schedulesService.getAllSchedules(weekStartDate, end.toISOString().split('T')[0]).then(r => {
            setWeekSchedules(r.data || []);
        });
        getActiveFaults();
    };

    const addToSchedule = (shelf) => {
        if (!scheduledShelves.find(s => s.name === shelf.name)) {
            const newScheduled = [...scheduledShelves, shelf];
            setScheduledShelves(newScheduled);
            console.log('Added to schedule:', shelf.name, 'New count:', newScheduled.length);
        }
    };

    const removeFromSchedule = (index) => {
        const newScheduled = [...scheduledShelves];
        newScheduled.splice(index, 1);
        setScheduledShelves(newScheduled);
    };

    const moveUp = (index) => {
        if (index === 0) return;
        const newScheduled = [...scheduledShelves];
        const temp = newScheduled[index];
        newScheduled[index] = newScheduled[index - 1];
        newScheduled[index - 1] = temp;
        setScheduledShelves(newScheduled);
    };

    const moveDown = (index) => {
        if (index === scheduledShelves.length - 1) return;
        const newScheduled = [...scheduledShelves];
        const temp = newScheduled[index];
        newScheduled[index] = newScheduled[index + 1];
        newScheduled[index + 1] = temp;
        setScheduledShelves(newScheduled);
    };

    const onDragStart = (e, index, type) => {
        e.dataTransfer.setData("index", index);
        e.dataTransfer.setData("type", type);
    };

    const onDragOver = (e) => {
        e.preventDefault();
    };

    const onDrop = (e, targetIndex) => {
        const sourceIndex = parseInt(e.dataTransfer.getData("index"));
        const sourceType = e.dataTransfer.getData("type");

        if (sourceType === "available") {
            const shelf = availableShelves[sourceIndex];
            if (!scheduledShelves.find(s => s.name === shelf.name)) {
                const newScheduled = [...scheduledShelves];
                if (targetIndex === -1) {
                    newScheduled.push(shelf);
                } else {
                    newScheduled.splice(targetIndex, 0, shelf);
                }
                setScheduledShelves(newScheduled);
            }
        } else if (sourceType === "scheduled") {
            const newScheduled = [...scheduledShelves];
            const movedItem = newScheduled.splice(sourceIndex, 1)[0];
            if (targetIndex === -1) {
                newScheduled.push(movedItem);
            } else {
                newScheduled.splice(targetIndex, 0, movedItem);
            }
            setScheduledShelves(newScheduled);
        }
    };

    const saveSchedule = () => {
        if (!selectedMachine || !selectedDate) {
            addToast(t('schedules.selectMachineAndDate'), { appearance: 'warning', autoDismiss: true });
            return;
        }
        if (scheduledShelves.length === 0) {
            if (!window.confirm(t('schedules.emptyScheduleConfirm'))) {
                return;
            }
        }
        setLoading(true);
        const shelfNames = scheduledShelves.map(s => s.name);
        console.log('Saving schedule for', selectedMachine.value, 'on', selectedDate, 'with shelves:', shelfNames);
        schedulesService.createOrUpdateSchedule(selectedMachine.value, selectedDate, shelfNames, dateTo || null).then(() => {
            addToast(t('schedules.saveSuccess'), { appearance: 'success', autoDismiss: true });
            setLoading(false);
            setShowForm(false);
            setSelectedMachine(null);
            setSelectedDate(new Date().toISOString().split('T')[0]);
            setScheduledShelves([]);
            setEditingScheduleId(null);
            setDateTo('');
            setFormFilterCategory(null);
            setFormFilterSubcategory(null);
            fetchWeekSchedules();
        }).catch(e => {
            addToast(t('schedules.saveError'), { appearance: 'error', autoDismiss: true });
            setLoading(false);
        });
    };

    const deleteSchedule = () => {
        if (!editingScheduleId) return;
        if (window.confirm(t('schedules.confirmDelete'))) {
            setLoading(true);
            schedulesService.deleteSchedule(editingScheduleId).then(() => {
                addToast(t('schedules.deleteSuccess'), { appearance: 'success', autoDismiss: true });
                setLoading(false);
                setShowForm(false);
                setSelectedMachine(null);
                setSelectedDate(new Date().toISOString().split('T')[0]);
                setScheduledShelves([]);
                setEditingScheduleId(null);
                setDateTo('');
                setFormFilterCategory(null);
                setFormFilterSubcategory(null);
                fetchWeekSchedules();
            }).catch(e => {
                addToast(t('schedules.deleteError'), { appearance: 'error', autoDismiss: true });
                setLoading(false);
            });
        }
    };

    const weekDays = [];
    const start = new Date(weekStartDate);
    for (let i = 0; i < 7; i++) {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        weekDays.push(d.toISOString().split('T')[0]);
    }

    const getDaySchedules = (dateStr) => {
        return weekSchedules.filter(s => {
            // Apply Date Filter
            let dateMatch = false;
            if (!s.dateTo) dateMatch = s.date === dateStr;
            else dateMatch = dateStr >= s.date && dateStr <= s.dateTo;
            if (!dateMatch) return false;

            // Apply Field Filters
            const machine = allMachinesRaw.find(m => m.Code === s.machineId);
            if (calFilterCategory && machine?.Code1 !== calFilterCategory.value) return false;
            if (calFilterSubcategory && machine?.Code2 !== calFilterSubcategory.value) return false;
            if (calFilterMachine && s.machineId !== calFilterMachine.value) return false;
            if (calFilterShelf && !s.shelfNames.includes(calFilterShelf.value)) return false;

            return true;
        });
    };

    const editSchedule = (s) => {
        const machine = machines.find(m => m.value === s.machineId);
        // Set form filters based on the selected machine to ensure it shows up correctly in the dropdown
        if (machine) {
            setFormFilterCategory(machine.Code1 ? { value: machine.Code1, label: machine.Code1 } : null);
            setFormFilterSubcategory(machine.Code2 ? { value: machine.Code2, label: machine.Code2 } : null);
        }
        setSelectedMachine(machine);
        setSelectedDate(s.date);
        setDateTo(s.dateTo || '');
        setScheduledShelves(s.shelfNames.map(name => ({ name })));
        setEditingScheduleId(s.id);
        setShowForm(true);
        if (formRef.current) {
            formRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const onDayClick = (dateStr) => {
        setSelectedDate(dateStr);
        setEditingScheduleId(null);
        if (selectedMachine) {
            fetchSchedule();
        } else {
            // Keep scheduledShelves if already there?
            // Actually onDayClick is usually for a fresh selection or changing date.
            // If I just click another day, I probably want to see that day's schedule.
            // But if I'm in "New Schedule" mode and I haven't saved, clicking another day might be intentional to change the start date.
        }
        setShowForm(true);
        if (formRef.current) {
            formRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const onDayMouseDown = (dateStr) => {
        setIsDraggingDates(true);
        setDragStartDate(dateStr);
        setDragEndDate(dateStr);
    };

    const onDayMouseEnter = (dateStr) => {
        if (isDraggingDates) {
            setDragEndDate(dateStr);
        }
    };

    const onDayMouseUp = (dateStr) => {
        if (isDraggingDates) {
            let start = dragStartDate;
            let end = dateStr;

            if (start > end) {
                [start, end] = [end, start];
            }

            setSelectedDate(start);
            if (start !== end) {
                setDateTo(end);
            } else {
                setDateTo('');
            }

            setEditingScheduleId(null);
            if (selectedMachine) {
                // We don't fetch schedule here if it's a range, 
                // but if it's a single day we should probably check if it exists
                if (start === end) {
                    fetchScheduleForDate(start);
                } else {
                    // Do not clear scheduledShelves if it's a range selection
                    // as the user might be intending to apply the current selection to this range
                }
            } else {
                // Keep current selection
            }

            setShowForm(true);
            if (formRef.current) {
                formRef.current.scrollIntoView({ behavior: 'smooth' });
            }

            setIsDraggingDates(false);
            setDragStartDate(null);
            setDragEndDate(null);
        }
    };

    const fetchScheduleForDate = (date) => {
        setLoading(true);
        schedulesService.getSchedule(selectedMachine.value, date).then(r => {
            if (r.data && r.data.shelfNames) {
                setScheduledShelves(r.data.shelfNames.map(name => ({ name })));
                if (r.data.dateTo) setDateTo(r.data.dateTo);
                else setDateTo('');
                setEditingScheduleId(r.data.id);
            } else {
                setEditingScheduleId(null);
            }
            setLoading(false);
        }).catch(e => {
            console.error(e);
            setLoading(false);
        });
    }

    const isInDragRange = (dateStr) => {
        if (!isDraggingDates || !dragStartDate || !dragEndDate) return false;
        let start = dragStartDate;
        let end = dragEndDate;
        if (start > end) [start, end] = [end, start];
        return dateStr >= start && dateStr <= end;
    };

    const changeWeek = (offset) => {
        const d = new Date(weekStartDate);
        d.setDate(d.getDate() + offset * 7);
        setWeekStartDate(d.toISOString().split('T')[0]);
    };

    // Filter Options
    const calCategoryOptions = [...new Set(allMachinesRaw.map(m => m.Code1).filter(Boolean))].map(c => ({ value: c, label: c }));
    const calSubcategoryOptions = [...new Set(allMachinesRaw
        .filter(m => !calFilterCategory || m.Code1 === calFilterCategory.value)
        .map(m => m.Code2).filter(Boolean))].map(c => ({ value: c, label: c }));
    const calMachineOptions = allMachinesRaw
        .filter(m => !calFilterCategory || m.Code1 === calFilterCategory.value)
        .filter(m => !calFilterSubcategory || m.Code2 === calFilterSubcategory.value)
        .map(m => ({ value: m.Code, label: `${m.Code} - ${m.Description}` }));
    const shelfOptions = availableShelves.map(s => ({ value: s.name, label: s.name }));

    const formCategoryOptions = [...new Set(allMachinesRaw.map(m => m.Code1).filter(Boolean))].map(c => ({ value: c, label: c }));
    const formSubcategoryOptions = [...new Set(allMachinesRaw
        .filter(m => !formFilterCategory || m.Code1 === formFilterCategory.value)
        .map(m => m.Code2).filter(Boolean))].map(c => ({ value: c, label: c }));
    
    // Filter out machines with active faults that disable them (isOperable: false)
    // For machines with operable faults (isOperable: true), display the fault indicator in the label
    const filteredFormMachines = machines
        .filter(m => {
            const hasDisabledFault = activeFaults.some(f => f.machineId === m.value && (f.isOperable === false || f.isOperable === 'false'));
            return !hasDisabledFault;
        })
        .filter(m => !formFilterCategory || m.Code1 === formFilterCategory.value)
        .filter(m => !formFilterSubcategory || m.Code2 === formFilterSubcategory.value)
        .map(m => {
            const operableFault = activeFaults.find(f => f.machineId === m.value && f.isOperable !== false && f.isOperable !== 'false');
            if (operableFault) {
                return {
                    ...m,
                    label: `${m.raw?.Description || m.label || m.value} ⚠️ [${t('schedules.faultIndicator') || 'Βλάβη'}: ${operableFault.reason}]`,
                    activeFault: operableFault
                };
            }
            return m;
        });

    const selectedMachineFault = selectedMachine ? activeFaults.find(f => f.machineId === selectedMachine.value) : null;
    const isSelectedMachineDisabled = selectedMachineFault && (selectedMachineFault.isOperable === false || selectedMachineFault.isOperable === 'false');

    return (
        <div className="p-4">
            <div className="mb-5">
                <Card className="mb-3">
                    <CardBody className="bg-light p-2">
                        <Row className="g-2">
                            <Col md={3}>
                                <Select
                                    isClearable
                                    placeholder={t('volumes.category') || "Category"}
                                    options={calCategoryOptions}
                                    value={calFilterCategory}
                                    onChange={(val) => { setCalFilterCategory(val); setCalFilterSubcategory(null); }}
                                    styles={{ menu: base => ({ ...base, zIndex: 9999 }) }}
                                />
                            </Col>
                            <Col md={3}>
                                <Select
                                    isClearable
                                    placeholder={t('volumes.subcategory') || "Subcategory"}
                                    options={calSubcategoryOptions}
                                    value={calFilterSubcategory}
                                    onChange={setCalFilterSubcategory}
                                    styles={{ menu: base => ({ ...base, zIndex: 9999 }) }}
                                />
                            </Col>
                            <Col md={3}>
                                <Select
                                    isClearable
                                    placeholder={t('sideBar.machines') || "Machine"}
                                    options={calMachineOptions}
                                    value={calFilterMachine}
                                    onChange={setCalFilterMachine}
                                    styles={{ menu: base => ({ ...base, zIndex: 9999 }) }}
                                />
                            </Col>
                            <Col md={3}>
                                <Select
                                    isClearable
                                    placeholder={t('volumes.quarryLevel') || "Shelf"}
                                    options={shelfOptions}
                                    value={calFilterShelf}
                                    onChange={setCalFilterShelf}
                                    styles={{ menu: base => ({ ...base, zIndex: 9999 }) }}
                                />
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4>{t('schedules.title')}</h4>
                    <div>
                        <Button color="secondary" size="sm" className="me-2" onClick={() => changeWeek(-1)}>{t('schedules.previous')}</Button>
                        <Button color="secondary" size="sm" onClick={() => changeWeek(1)}>{t('schedules.next')}</Button>
                    </div>
                </div>
                <Table bordered responsive>
                    <thead>
                        <tr className="bg-light">
                            {weekDays.map(d => (
                                <th key={d} style={{ width: '14.28%' }}>
                                    {new Date(d).toLocaleDateString('el-GR', { weekday: 'short', day: 'numeric', month: 'numeric' })}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {weekDays.map(d => {
                                const isSelected = selectedDate === d || (dateTo && d >= selectedDate && d <= dateTo);
                                const isBeingDragged = isInDragRange(d);
                                let backgroundColor = 'transparent';
                                if (isBeingDragged) backgroundColor = '#e0f0ff';
                                else if (isSelected) backgroundColor = '#f0f7ff';

                                return (
                                    <td 
                                        key={d} 
                                        onMouseDown={() => onDayMouseDown(d)}
                                        onMouseEnter={() => onDayMouseEnter(d)}
                                        onMouseUp={() => onDayMouseUp(d)}
                                        onClick={() => onDayClick(d)} 
                                        style={{ 
                                            verticalAlign: 'top', 
                                            cursor: 'pointer', 
                                            backgroundColor, 
                                            minWidth: '120px',
                                            userSelect: 'none'
                                        }}
                                    >
                                        <div style={{ minHeight: '120px' }}>
                                            {getDaySchedules(d).map(s => {
                                                const fault = activeFaults.find(f => f.machineId === s.machineId);
                                                return (
                                                    <div 
                                                        key={s.id} 
                                                        className={`p-1 mb-1 ${fault ? (fault.isOperable === false || fault.isOperable === 'false' ? 'bg-danger text-white' : 'bg-warning text-dark') : 'bg-primary text-white'} rounded small shadow-sm`}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => { e.stopPropagation(); editSchedule(s); }}
                                                        title={s.shelfNames.join(', ') + (fault ? `\n⚠️ [${t('schedules.faultIndicator') || 'Βλάβη'}: ${fault.reason}]` : '')}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <strong>{s.machineId}</strong>
                                                            {fault && <span title={fault.reason}>⚠️</span>}
                                                        </div>
                                                        <div style={{ fontSize: '0.8em' }}>{s.shelfNames.length} {t('schedules.levels')}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>
                    </tbody>
                </Table>
            </div>

            <div ref={formRef} className="d-flex justify-content-between align-items-center mb-3 pt-3 border-top">
                <h3>{t('schedules.daySchedule')}</h3>
                {!showForm && <Button color="primary" onClick={() => {
                    setSelectedMachine(null);
                    setSelectedDate(new Date().toISOString().split('T')[0]);
                    setDateTo('');
                    setScheduledShelves([]);
                    setEditingScheduleId(null);
                    setShowForm(true);
                }}>{t('schedules.newSchedule')}</Button>}
                {showForm && <Button color="secondary" onClick={() => setShowForm(false)}>{t('schedules.cancel')}</Button>}
            </div>
            
            {showForm && <>
            <Card className="mb-4">
                <CardBody>
                    <Row>
                        <Col md={3}>
                            <FormGroup>
                                <Label>{t('volumes.category') || "Category"}</Label>
                                <Select
                                    isClearable
                                    options={formCategoryOptions}
                                    value={formFilterCategory}
                                    onChange={(val) => { setFormFilterCategory(val); setFormFilterSubcategory(null); }}
                                    placeholder={t('schedules.selectCategory')}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label>{t('volumes.subcategory') || "Subcategory"}</Label>
                                <Select
                                    isClearable
                                    options={formSubcategoryOptions}
                                    value={formFilterSubcategory}
                                    onChange={setFormFilterSubcategory}
                                    placeholder={t('schedules.selectSubcategory')}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label>{t('schedules.machine')}</Label>
                                <Select
                                    options={filteredFormMachines}
                                    value={filteredFormMachines.find(m => m.value === selectedMachine?.value) || selectedMachine}
                                    onChange={setSelectedMachine}
                                    placeholder={t('schedules.selectMachine')}
                                />
                                {selectedMachineFault && !isSelectedMachineDisabled && (
                                    <Alert color="warning" className="mt-2 p-2 small mb-0">
                                        <strong>⚠️ {t('schedules.machineHasFault') || 'Ενεργή Βλάβη'}:</strong> {selectedMachineFault.reason}
                                        {selectedMachineFault.message ? ` (${selectedMachineFault.message})` : ''}
                                        {' '}<Badge color="info">{t('faults.operableShort') || 'Λειτουργικό'}</Badge>
                                    </Alert>
                                )}
                                {isSelectedMachineDisabled && (
                                    <Alert color="danger" className="mt-2 p-2 small mb-0">
                                        <strong>⚠️ {t('schedules.disabledMachineWarning') || 'Το μηχάνημα είναι εκτός λειτουργίας λόγω βλάβης και δεν μπορεί να προγραμματιστεί.'}</strong>
                                    </Alert>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label>{t('schedules.date')}</Label>
                                <Input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label>{t('schedules.dateTo')}</Label>
                                <Input
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                    min={selectedDate}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            <Row className="mb-5">
                <Col md={6}>
                    <Card style={{ height: '400px' }}>
                        <CardBody style={{ overflowY: 'auto' }}>
                            <h5>{t('schedules.availableShelves')}</h5>
                            <ListGroup>
                                {availableShelves.map((shelf, index) => (
                                    <ListGroupItem 
                                        key={shelf.id || shelf.name} 
                                        className="d-flex justify-content-between align-items-center"
                                        draggable
                                        onDragStart={(e) => onDragStart(e, index, "available")}
                                        style={{ cursor: 'grab' }}
                                    >
                                        {shelf.name}
                                        <Button color="primary" size="sm" onClick={() => addToSchedule(shelf)}>{t('schedules.add')}</Button>
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card style={{ height: '400px' }}>
                        <CardBody 
                            onDragOver={onDragOver}
                            onDrop={(e) => onDrop(e, -1)}
                            style={{ overflowY: 'auto' }}
                        >
                            <h5>{t('schedules.scheduledShelves')}</h5>
                            <ListGroup>
                                {scheduledShelves.map((shelf, index) => (
                                    <ListGroupItem 
                                        key={`${shelf.name}-${index}`} 
                                        className="d-flex justify-content-between align-items-center"
                                        draggable
                                        onDragStart={(e) => onDragStart(e, index, "scheduled")}
                                        onDragOver={onDragOver}
                                        onDrop={(e) => {
                                            e.stopPropagation();
                                            onDrop(e, index);
                                        }}
                                        style={{ cursor: 'move' }}
                                    >
                                        <div>
                                            <span className="me-2 font-weight-bold">{index + 1}.</span>
                                            {shelf.name}
                                        </div>
                                        <div>
                                            <Button color="light" size="sm" className="me-1" onClick={() => moveUp(index)} disabled={index === 0}>↑</Button>
                                            <Button color="light" size="sm" className="me-1" onClick={() => moveDown(index)} disabled={index === scheduledShelves.length - 1}>↓</Button>
                                            <Button color="danger" size="sm" onClick={() => removeFromSchedule(index)}>X</Button>
                                        </div>
                                    </ListGroupItem>
                                ))}
                                {scheduledShelves.length === 0 && <ListGroupItem className="text-center text-muted">{t('schedules.none')}</ListGroupItem>}
                            </ListGroup>
                        </CardBody>
                        <div className="p-3 bg-light border-top d-flex">
                            <Button color="success" className="flex-grow-1 me-2" onClick={saveSchedule} disabled={loading || !selectedMachine || isSelectedMachineDisabled}>
                                {loading ? t('schedules.saving') : t('schedules.save')}
                            </Button>
                            {editingScheduleId && (
                                <Button color="danger" onClick={deleteSchedule} disabled={loading}>
                                    {t('schedules.delete')}
                                </Button>
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>
            </>}
        </div>
    );
};

export default withTranslation(Schedules);
